import { Dispatch, RefObject, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { FormError, validateProperty } from 'src/helpers/FormHelper';
import { PropertyHelper } from 'src/helpers/PropertyHelper';
import { IProperty } from 'src/interfaces/Property';
import { useNotifications } from 'src/providers/notificationsProvider/NotificationsConsumer';
import { SETTINGS_SECTION_TYPES } from 'src/types/SettingsSectionTypes';

import { savePropertyDraft } from '../propertySettingsSaveOrchestrator';
import { markSaved, PropertySettingsAction, setErrors, setSaving } from '../state';

type ErrorRefs = RefObject<Partial<Record<SETTINGS_SECTION_TYPES, HTMLElement>>>;

interface SaveParams {
	draft: IProperty;
	isListMode: boolean;
	shouldCrawl?: boolean;
}

interface UsePropertySaveParams {
	propertyId: string;
	dispatch: Dispatch<PropertySettingsAction>;
	onSaved: (savedPropertyId: string) => void;
	errorRefs: ErrorRefs;
}

export type SaveFn = (params: SaveParams) => Promise<void>;

function scrollToFirstError(errorRefs: ErrorRefs): void {
	requestAnimationFrame(() => {
		const firstErrorElement = Object.values(errorRefs.current)
			.filter((el): el is HTMLElement => Boolean(el))
			.reduce<HTMLElement | null>(
				(topElement, element) =>
					!topElement ||
					element.getBoundingClientRect().top < topElement.getBoundingClientRect().top
						? element
						: topElement,
				null
			);
		firstErrorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	});
}

export function usePropertySave({
	propertyId,
	dispatch,
	onSaved,
	errorRefs
}: UsePropertySaveParams): SaveFn {
	const notificationsContext = useNotifications();
	const queryClient = useQueryClient();

	return useCallback(
		async ({ draft, isListMode, shouldCrawl = false }: SaveParams): Promise<void> => {
			const formErrors: FormError[] = validateProperty(draft, isListMode);
			dispatch(setErrors(formErrors));

			if (formErrors.length > 0) {
				scrollToFirstError(errorRefs);
				notificationsContext.alert({
					title: 'Validation failed',
					errorMessage: 'Please fix the highlighted errors before saving.'
				});
				return;
			}

			dispatch(setSaving(true));

			try {
				const savedProperty = await savePropertyDraft({
					draft,
					propertyId,
					shouldCrawl,
					isListMode
				});

				const savedId = savedProperty.id || propertyId;
				queryClient.removeQueries({ queryKey: ['propertySettings', savedId] });

				dispatch(markSaved());
				onSaved(savedId);
			} catch (err) {
				dispatch(setSaving(false));
				notificationsContext.alert({
					title: PropertyHelper.isPropertyInDraftMode(propertyId)
						? 'Cannot create property'
						: 'Cannot save property',
					serverError: err
				});
			}
		},
		[propertyId, dispatch, onSaved, notificationsContext, queryClient, errorRefs]
	);
}
